import { IconButton, Tooltip } from '@mui/material';
import { Straighten as Ruler } from '@mui/icons-material';
import { MeasureDistanceMode, ViewMode } from '@nebula.gl/edit-modes';
import { EditableGeoJsonLayer } from '@nebula.gl/layers';
import clsx from 'clsx';
import { MapContext } from 'components/maps/MapContext';
import React, { memo, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapTool } from '../const';
import useLayersToolbarStyles from '../styles';
import { DistanceToolProps } from './types';
import { useHotKey, useUnitPreference } from 'kineticUtils/hooks';
import { EditableGeoJsonLayerProps } from 'components/maps/BaseMap/DeckGlMapContainer/types';

export const DistanceTool: React.FunctionComponent<DistanceToolProps> = () => {
  const { t } = useTranslation('app');
  const classes = useLayersToolbarStyles();
  const { activeMapTool, setExtraDeckGlLayers, setActiveMapTool } = useContext(MapContext);

  const [mode, setMode] = React.useState(() => MeasureDistanceMode);
  const unitPreference = useUnitPreference();
  const isActive = activeMapTool === MapTool.DISTANCE;

  useHotKey({
    key: 'd',
    alt: true,
    ctrl: true,
    callback: () => setActiveMapTool(MapTool.DISTANCE),
  });

  useEffect(() => {
    setExtraDeckGlLayers((prev) => {
      const toSave = [...prev];
      const layer = new EditableGeoJsonLayer({
        id: 'distance-layer',
        modeConfig: {
          turfOptions: {
            units: unitPreference.unit === 'metric' ? 'kilometers' : 'miles',
          },
        },
        mode: !isActive ? ViewMode : mode,
        order: 4,
      } as EditableGeoJsonLayerProps);

      const index = toSave.findIndex((layer) => layer.id === 'distance-layer');
      if (index > -1) {
        toSave.splice(index, 1);
      }
      toSave.push(layer);
      return toSave;
    });
  }, [isActive, mode, setExtraDeckGlLayers, unitPreference.unit]);

  return (
    <>
      <Tooltip title={t('distanceTool')}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setActiveMapTool(MapTool.DISTANCE);
            setMode(() => MeasureDistanceMode);
          }}
          className={clsx(classes.button, { [classes.buttonActive]: isActive })}
          sx={{ padding: '9px' }}
        >
          <Ruler fontSize='small' />
        </IconButton>
      </Tooltip>
    </>
  );
};

export default memo(DistanceTool);
